import React, {Component} from 'react';
import {connect} from 'react-redux';
import axios from 'axios';
import {withRouter} from 'react-router-dom';
import Backdrop from '@material-ui/core/Backdrop';
import ManageDataDictionary from './components/dd-manage';
import DDWizard from './wizard/dd-wizard';
import DDDetailsView from './components/dd-details-view';
import Snackbar from '@material-ui/core/Snackbar';
import Banner from '../../components/banner';
import Modal from '../../components/modal';
import ErrorBoundary from '../../components/error-boundary';
import Auth from '../../auth/auth';
import {loadDictionaryList, deleteDictionary, loadDictionaryDetails, updateDictionaryInstance, importSchema, fetchUserList, fetchTechOwnerList, refreshWithDiff, loadPendingChanges, applyWorkflowAction, rollbackChangeRequest, loadRollbackHistory, updateColumnIsPii, updateColumnIsAiEligible, loadMissingObjects, previewMissingAttributes, importMissingObjects} from './data-dictionary.actions';
import {setUnsavedChanges, clearUnsavedChanges} from '../../common-redux/common.actions';
import {confirmNavigation} from '../../util/nav-confirm';


const auth = new Auth();
let orgId;

const OBJECT_TYPES = ['tables', 'views', 'storedProcedures', 'functions', 'triggers'];
const ddVal = (v, fallback = '') => (v === undefined || v === null ? fallback : v);
// Normalize owner userIds to strings before comparing. Owners edited in the UI
// carry string ids (post-#611), while legacy stored owners may carry numeric
// ids; without this a numeric-vs-string id would read as a phantom owner change.
const ddOwnerIds = (o) => (Array.isArray(o) ? o : []).map((x) => (
  x && typeof x === 'object' ? {...x, userId: x.userId == null ? '' : String(x.userId)} : x
));
const ddOwners = (o) => JSON.stringify(ddOwnerIds(o));
const ddBool = (v) => !!v;
const objName = (o) => o.objectName || o.object_name || o.tableName || o.table_name || o.name || 'object';
const colName = (c) => c.columnName || c.column_name || c.name || 'column';

// Builds a human-readable list of unsaved changes by diffing the original
// dictionary details (props.dictionaryDetails) against the edited copy.
// Mirrors the field comparisons in handleSaveDictionaryUpdates.
export function summarizeDictionaryChanges(original, edited) {
  if (!edited) return [];
  const labels = [];
  const orig = original || {};

  const oInst = orig.instance || {};
  const eInst = edited.instance || {};
  if (ddVal(oInst.instanceName || oInst.instance_name) !== ddVal(eInst.instanceName || eInst.instance_name)) {
    labels.push('Renamed dictionary instance');
  }
  if (ddVal(oInst.description) !== ddVal(eInst.description)) {
    labels.push('Modified instance description');
  }

  // Index original objects/columns by id for O(1) comparison.
  const objMap = {};
  const colMap = {};
  (orig.schemas || []).forEach((s) => OBJECT_TYPES.forEach((t) => (s[t] || []).forEach((o) => {
    const id = o.objectId || o.object_id;
    if (id) objMap[id] = o;
    (o.columns || []).forEach((c) => {
      const cid = c.columnId || c.column_id;
      if (cid) colMap[cid] = c;
    });
  })));

  (edited.schemas || []).forEach((s) => OBJECT_TYPES.forEach((t) => (s[t] || []).forEach((o) => {
    const oo = objMap[o.objectId || o.object_id];
    if (oo) {
      const name = objName(o);
      if (ddVal(oo.businessContext || oo.business_context) !== ddVal(o.businessContext || o.business_context)) {
        labels.push(`Modified business context on '${name}'`);
      }
      if (ddOwners(oo.businessOwner ?? oo.business_owner) !== ddOwners(o.businessOwner ?? o.business_owner)) {
        labels.push(`Changed business owner on '${name}'`);
      }
      if (ddOwners(oo.techOwner ?? oo.tech_owner) !== ddOwners(o.techOwner ?? o.tech_owner)) {
        labels.push(`Changed tech owner on '${name}'`);
      }
    }
    (o.columns || []).forEach((c) => {
      const oc = colMap[c.columnId || c.column_id];
      if (!oc) return;
      const name = colName(c);
      if (ddVal(oc.businessContext || oc.business_context) !== ddVal(c.businessContext || c.business_context)) {
        labels.push(`Modified business context on '${name}'`);
      }
      if (ddBool(oc.isPii ?? oc.is_pii) !== ddBool(c.isPii ?? c.is_pii)) {
        labels.push(`${ddBool(c.isPii ?? c.is_pii) ? 'Marked' : 'Unmarked'} '${name}' as PII`);
      }
      if (ddBool(oc.isAiEligible ?? oc.is_ai_eligible) !== ddBool(c.isAiEligible ?? c.is_ai_eligible)) {
        labels.push(`${ddBool(c.isAiEligible ?? c.is_ai_eligible) ? 'Marked' : 'Unmarked'} '${name}' as AI-eligible`);
      }
    });
  })));

  return labels;
}


export class DataDictionary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pageNavContainer: 'hide',
      pageNavContainerMain: 'hide',
      backdrop: false,
      dictionaryId: null,
      dictionaryData: {name: '', description: '', type: null},
      snackbarStatus: false,
      bannerType: 'danger',
      showBanner: false,
      bannerTitle: '',
      bannerMessage: '',
      actionTriggered: false,
      triggeredItemName: '',
      actionName: '',
      modalOpen: false,
      modalTitle: '',
      modalState: '',
      modalContent: '',
      modalPrimaryButton: '',
      modalSecondaryButton: '',
      dictionaryTitle: '',
      saveDictionaryId: null,
      pageState: 0,
      editedDictionaryDetails: null,
    };
    orgId = auth.getOrgId();
  }


  componentDidMount = () => {
    // Load dictionary list when component mounts
    const {loadDictionaryList} = this.props;
    if (loadDictionaryList) {
      loadDictionaryList();
    }

    // Show main nav for landing page
    this.setState({pageNavContainerMain: 'page-nav-container'});
  };

  componentWillUnmount() {
    // Drop the navigation guard when leaving the page.
    if (this.props.clearUnsavedChanges) this.props.clearUnsavedChanges();
  }

  componentDidUpdate(prevProps, prevState) {
    // Keep the cross-page unsaved-changes guard in sync with local edits.
    if (prevState && this.props.setUnsavedChanges &&
        prevState.editedDictionaryDetails !== this.state.editedDictionaryDetails) {
      this.props.setUnsavedChanges(
          summarizeDictionaryChanges(this.props.dictionaryDetails, this.state.editedDictionaryDetails),
      );
    }

    // Handle action completion like database-connection does
    if (prevProps.isLoading !== this.props.isLoading && this.state.actionTriggered === true) {
      // Check if import/save completed successfully
      if (prevProps.isLoading.includes('importschema') && !this.props.isLoading.includes('importschema')) {
        const hasNewError = this.props.error && this.props.error.length > 0;
        if (!hasNewError) {
          this.setState({actionTriggered: false});
          // If we're in edit mode (dictionaryId exists), call updateSuccess instead of saveSuccess
          if (this.state.dictionaryId) {
            this.updateSuccess();
          } else {
            this.saveSuccess();
          }
        } else {
          this.setState({actionTriggered: false, showBanner: true, bannerType: 'danger',
            bannerTitle: 'Error', bannerMessage: 'Failed to save dictionary.'});
        }
      }

      // Check if delete completed successfully
      if (prevProps.isLoading.includes('deletedictionary') && !this.props.isLoading.includes('deletedictionary')) {
        const hasNewError = this.props.error && this.props.error.length > 0;
        if (!hasNewError) {
          this.setState({actionTriggered: false});
          this.deleteSuccess();
        } else {
          this.setState({actionTriggered: false, showBanner: true, bannerType: 'danger',
            bannerTitle: 'Error', bannerMessage: 'Failed to delete dictionary.'});
        }
      }

      // Check if update completed successfully
      if (prevProps.isLoading.includes('updatedictionary') && !this.props.isLoading.includes('updatedictionary')) {
        const hasNewError = this.props.error && this.props.error.length > 0;
        if (!hasNewError) {
          this.setState({actionTriggered: false, editedDictionaryDetails: null});
          this.updateSuccess();
        } else {
          this.setState({
            actionTriggered: false,
            snackbarStatus: false,
            showBanner: true,
            bannerType: 'danger',
            bannerTitle: 'Error',
            bannerMessage: 'Failed to update dictionary.',
          });
        }
      }
    }

    // Handle import-missing completion (independent of actionTriggered)
    if (prevProps.isImportingMissing && !this.props.isImportingMissing) {
      const result = this.props.importMissingResult;
      if (result && typeof result === 'object') {
        const insertedObjects = result.insertedObjects || 0;
        const updatedAttributeCount = (result.updatedAttributes || []).reduce((sum, item) => sum + (item.updatedRows || 0), 0);
        const conflictCount = (result.conflictedAttributes || []).reduce((sum, item) => sum + (item.count || 0), 0);
        const importedMsg = `Imported ${insertedObjects} object${insertedObjects !== 1 ? 's' : ''} and backfilled ${updatedAttributeCount} attribute value${updatedAttributeCount !== 1 ? 's' : ''}. No approval required.`;
        const conflictSummary = conflictCount > 0 ?
          ` ${conflictCount} conflict${conflictCount !== 1 ? 's' : ''} skipped.` :
          '';
        const conflictDetails = (result.conflictedAttributes || []).slice(0, 5).map((item) => {
          return `${item.category}: ${item.fieldName} (${item.count})`;
        });
        const detailText = conflictDetails.length > 0 ? ` Conflicts: ${conflictDetails.join(', ')}.` : '';
        this.setState({
          showBanner: true,
          bannerType: 'success',
          bannerTitle: insertedObjects > 0 ? 'Import Complete' : 'Import Finished',
          bannerMessage: importedMsg + conflictSummary + detailText,
        });
        // Reload dictionary details so summary cards refresh
        if (this.state.dictionaryId) {
          this.props.loadDictionaryDetails(this.state.dictionaryId);
          this.props.loadPendingChanges(this.state.dictionaryId);
        }
      } else {
        // Error case: importMissingResult is null (IMPORT_MISSING_FAILURE dispatched)
        this.setState({
          showBanner: true,
          bannerType: 'danger',
          bannerTitle: 'Import Failed',
          bannerMessage: 'Failed to import missing objects. Please check your database connection and try again.',
        });
      }
    }

    // Refresh-with-diff finished (the POST resolved => all change_request rows are
    // committed). Reload the now-complete pending set here instead of racing it on a
    // fixed timer, which read a partial set while the backend was still writing.
    // Fires on failure too (a mid-run failure may have committed some rows).
    if (prevProps.isRefreshing && !this.props.isRefreshing) {
      // ponytail: last-write-wins against selectDictionary's early load on the
      // list-view path; no request sequencing, so a >2-3s straggler could overwrite.
      // Acceptable — overlapping refreshes are prevented by disabled={isRefreshing}.
      if (this.state.dictionaryId) {
        this.props.loadPendingChanges(this.state.dictionaryId);
      }
      // Backend soft-failures come back as REFRESH_WITH_DIFF_SUCCESS with response:''
      // (route sends HTTP 200 {status:false}), network errors as FAILURE with null —
      // both leave refreshResult falsy, so the falsy check is load-bearing.
      if (!this.props.refreshResult) {
        this.setState({
          showBanner: true,
          bannerType: 'danger',
          bannerTitle: 'Refresh Failed',
          bannerMessage: 'Schema refresh could not be completed. Please try again.',
        });
      }
    }
  }

  saveSuccess = () => {
    const {loadDictionaryList} = this.props;
    loadDictionaryList();
    this.navigateToBegin();
    const message = this.state.triggeredItemName + ' has been added successfully.';
    this.setState({showBanner: true, bannerType: 'success', bannerTitle: 'Done!', bannerMessage: message});
    this.goToTop();
  };

  updateSuccess = () => {
    const {loadDictionaryList} = this.props;
    loadDictionaryList();
    this.navigateToBegin();
    const message = this.state.triggeredItemName + ' has been updated successfully.';
    this.setState({
      showBanner: true,
      bannerType: 'success',
      bannerTitle: 'Done!',
      bannerMessage: message,
      snackbarStatus: false,
    });
    this.goToTop();
  };

  deleteSuccess = () => {
    const {loadDictionaryList} = this.props;
    loadDictionaryList();
    const workflowActionsDeleted = this.props.deleteResult?.workflowActionsDeleted ?? 0;
    let message = this.state.triggeredItemName + ' has been deleted successfully.';
    if (workflowActionsDeleted > 0) {
      message += ` ${workflowActionsDeleted} pending workflow action${workflowActionsDeleted === 1 ? '' : 's'} ${workflowActionsDeleted === 1 ? 'was' : 'were'} also removed.`;
    }
    this.setState({showBanner: true, bannerType: 'success', bannerTitle: 'Done!', bannerMessage: message});
    this.goToTop();
  };

  navigateToBegin = () => {
    this.props.parentNavBar(1);
    this.setState({
      pageState: 0,
      pageNavContainer: 'hide',
      pageNavContainerMain: 'page-nav-container',
    });
  };

  navigateToSuperUserPanel = () => {
    const isSettingsPath = this.props.location?.pathname?.startsWith('/settings');
    if (this.props.history?.push && isSettingsPath) {
      this.props.history.push('/settings');
    }
    this.props.parentNavBar(0);
  };

  cancelFlow = () => {
    this.navigateToBegin();
  };

  saveDictionaryFlow = (data, flowName, dictionaryName) => {
    // TODO: Implement API call
    this.setState({actionTriggered: true, triggeredItemName: dictionaryName, saveDictionaryId: data.id});

    // Temporary localStorage save
    const {loadDictionaryList} = this.props;
    try {
      const list = this.props.dictionaryList || [];
      if (flowName === 'new') {
        const newItem = {...data, id: Date.now(), createdAt: new Date().toISOString()};
        list.push(newItem);
      } else {
        const idx = list.findIndex((d) => d.id === data.id);
        if (idx !== -1) {
          list[idx] = {...list[idx], ...data};
        }
      }
      localStorage.setItem('dataDictionaryList', JSON.stringify(list));
      loadDictionaryList();
      flowName === 'new' ? this.saveSuccess() : this.updateSuccess();
    } catch (e) {
      console.error('Error saving dictionary', e);
    }
  };

  deleteDictionary = (data) => {
    this.setState({actionTriggered: true, triggeredItemName: data.dictionaryName});

    // Call the Redux action to delete from server
    this.props.deleteDictionary(data.dictionaryId);
  };

  toggleBackdrop = () => {
    this.state.backdrop ?
    this.setState({backdrop: false, pageNavContainer: 'page-nav-container'}) :
    this.setState({backdrop: true, pageNavContainer: 'page-nav-container reduce-zindex'});
  };

  hideBackNavigation = () => {
    this.setState({
      pageNavContainer: 'hide',
    });
  };

  goToTop = () => {
    document.getElementById('app').scrollTo(0, 0);
  };

  dismissBanner = () => {
    this.setState({showBanner: false});
  };

  snackHandler = (state) => {
    this.setState({snackbarStatus: state});
  };

  modalClose = () => {
    this.setState({
      modalOpen: false,
    });
  };

  modalConfirm = () => {
    this.setState({
      modalOpen: false,
    });
  };

  selectDictionary = (value) => {
    this.dismissBanner();
    this.props.parentNavBar(2);

    if (value === 'new') {
      const isSettingsPath = this.props.location?.pathname?.startsWith('/settings');
      if (this.props.history?.push && isSettingsPath) {
        this.props.history.push('/settings/datadictionary/new');
      }
      // Clear any saved wizard state for fresh start
      try {
        localStorage.removeItem('ddWizardState');
      } catch (e) {
        console.error('Error clearing wizard state', e);
      }
      this.setState({
        pageState: 1,
        pageNavContainer: 'page-nav-container overlap',
        pageNavContainerMain: 'hide',
        dictionaryId: null,
        dictionaryTitle: 'Add to Dictionary',
      });
    } else {
      // Load dictionary details from API

      // API returns snake_case field names
      const instanceId = value.dictionary.instance_id || value.dictionary.instanceId || value.dictionary.id;
      const instanceName = value.dictionary.instance_name || value.dictionary.instanceName || value.dictionary.name;


      if (!instanceId) {
        console.error('ERROR: instanceId is undefined! Dictionary object:', value.dictionary);
        return;
      }

      this.props.loadDictionaryDetails(instanceId);
      this.props.loadPendingChanges(instanceId);

      const isSettingsPath = this.props.location?.pathname?.startsWith('/settings');
      if (this.props.history?.push && isSettingsPath) {
        this.props.history.push(`/settings/datadictionary/${instanceId}`);
      }

      this.setState({
        pageState: 1,
        pageNavContainer: 'page-nav-container overlap',
        pageNavContainerMain: 'hide',
        dictionaryTitle: `View & Edit: ${instanceName}`,
        dictionaryId: instanceId,
      }, () => {
      });
    }
  };

  handleWizardSave = (payload) => {
    // Scroll to top
    this.goToTop();

    // Save the dictionary - use instance.instanceName from the payload
    const dictionaryName = payload.instance?.instanceName || payload.name || 'Dictionary';
    this.saveDictionaryFlow(payload, 'new', dictionaryName);
  };

  handleWizardCancel = () => {
    this.navigateToBegin();
  };

  handleUpdateBusinessContext = (itemType, itemIndex, businessContext) => {
    // Update the edited dictionary details
    const editedDetails = this.state.editedDictionaryDetails || JSON.parse(JSON.stringify(this.props.dictionaryDetails));

    if (editedDetails && editedDetails[itemType] && editedDetails[itemType][itemIndex]) {
      editedDetails[itemType][itemIndex].businessContext = businessContext;
      this.setState({editedDictionaryDetails: editedDetails});
    }
  };

  handleUpdateOwner = (itemType, itemIndex, businessOwner) => {
    // Update the owner (businessOwner) in edited dictionary details
    const editedDetails = this.state.editedDictionaryDetails || JSON.parse(JSON.stringify(this.props.dictionaryDetails));

    if (editedDetails && editedDetails[itemType] && editedDetails[itemType][itemIndex]) {
      editedDetails[itemType][itemIndex].businessOwner = businessOwner;
      this.setState({editedDictionaryDetails: editedDetails});
    }
  };

  handleUpdateTechOwner = (itemType, itemIndex, techOwner) => {
    // Update the tech owner in edited dictionary details
    const editedDetails = this.state.editedDictionaryDetails || JSON.parse(JSON.stringify(this.props.dictionaryDetails));

    if (editedDetails && editedDetails[itemType] && editedDetails[itemType][itemIndex]) {
      editedDetails[itemType][itemIndex].techOwner = techOwner;
      this.setState({editedDictionaryDetails: editedDetails});
    }
  };

  handleUpdateItem = (itemType, objectId, updatedItem) => {
    const editedDetails = this.state.editedDictionaryDetails ?
      JSON.parse(JSON.stringify(this.state.editedDictionaryDetails)) :
      JSON.parse(JSON.stringify(this.props.dictionaryDetails));

    if (!editedDetails || !editedDetails.schemas) return;

    const schemaTypeMap = {
      'BASE TABLE': 'tables',
      'TABLE': 'tables',
      'VIEW': 'views',
      'STORED_PROCEDURE': 'storedProcedures',
      'PROCEDURE': 'storedProcedures',
      'FUNCTION': 'functions',
      'TRIGGER': 'triggers',
    };
    const schemaKey = schemaTypeMap[itemType];
    // Match the object by stable id, not by list position: the drawer's index was
    // relative to a *filtered* grid but this list is unfiltered, so the old
    // counter walk wrote to the wrong object under any active filter (#635).
    if (!schemaKey || !objectId) return;

    let found = false;
    editedDetails.schemas.forEach((schema) => {
      if (found) return;
      (schema[schemaKey] || []).forEach((obj, idx) => {
        if (found) return;
        if ((obj.objectId || obj.object_id) === objectId) {
          // Merge all fields from updatedItem into obj
          schema[schemaKey][idx] = {...obj, ...updatedItem};
          found = true;
        }
      });
    });

    this.setState({editedDictionaryDetails: editedDetails});
  };

  handleUpdateColumnIsPii = (columnId, isPii) => {
    const source = this.state.editedDictionaryDetails || this.props.dictionaryDetails;
    if (!source) return Promise.resolve();
    const details = JSON.parse(JSON.stringify(source));
    if (details.schemas) {
      details.schemas.forEach((schema) => {
        ['tables', 'views', 'storedProcedures', 'functions', 'triggers'].forEach((type) => {
          (schema[type] || []).forEach((item) => {
            (item.columns || []).forEach((col) => {
              if (col.columnId === columnId) col.isPii = isPii;
            });
          });
        });
      });
    }
    this.setState({editedDictionaryDetails: details});
    return Promise.resolve();
  };

  handleUpdateColumnBusinessContext = (columnId, businessContext) => {
    const source = this.state.editedDictionaryDetails || this.props.dictionaryDetails;
    if (!source) return;
    const details = JSON.parse(JSON.stringify(source));
    if (details.schemas) {
      details.schemas.forEach((schema) => {
        ['tables', 'views', 'storedProcedures', 'functions', 'triggers'].forEach((type) => {
          (schema[type] || []).forEach((item) => {
            (item.columns || []).forEach((col) => {
              if (col.columnId === columnId) col.businessContext = businessContext;
            });
          });
        });
      });
    }
    this.setState({editedDictionaryDetails: details});
  };

  handleUpdateColumnIsAiEligible = (columnId, isAiEligible) => {
    const source = this.state.editedDictionaryDetails || this.props.dictionaryDetails;
    if (!source) return Promise.resolve();
    const details = JSON.parse(JSON.stringify(source));
    if (details.schemas) {
      details.schemas.forEach((schema) => {
        ['tables', 'views', 'storedProcedures', 'functions', 'triggers'].forEach((type) => {
          (schema[type] || []).forEach((item) => {
            (item.columns || []).forEach((col) => {
              if (col.columnId === columnId) col.isAiEligible = isAiEligible;
            });
          });
        });
      });
    }
    this.setState({editedDictionaryDetails: details});
    return Promise.resolve();
  };

  handleSaveDictionaryUpdates = (updatedDetails, pendingImportPayload) => {
    // Scroll to top
    this.goToTop();

    // Use updated details if provided, otherwise use state
    const detailsToSave = updatedDetails || this.state.editedDictionaryDetails || this.props.dictionaryDetails;

    if (!detailsToSave || !detailsToSave.instance) {
      console.error('No dictionary details to save');
      return;
    }

    const originalDetails = this.props.dictionaryDetails || {};
    const instanceId = detailsToSave.instance.instanceId || detailsToSave.instance.instance_id;
    const instanceName = detailsToSave.instance.instanceName || detailsToSave.instance.instance_name;
    const objectTypes = ['tables', 'views', 'storedProcedures', 'functions', 'triggers'];
    const normalizeOwners = (owners) => (Array.isArray(owners) ? owners : []);
    const normalizeBool = (value) => !!value;
    const getValue = (value, fallback = '') => (value === undefined || value === null ? fallback : value);
    const toDbObjectType = (type) => {
      const map = {
        'BASE TABLE': 'table',
        'TABLE': 'table',
        'VIEW': 'view',
        'STORED_PROCEDURE': 'stored_procedure',
        'PROCEDURE': 'stored_procedure',
        'FUNCTION': 'function',
        'TRIGGER': 'trigger',
      };
      const upper = (type || '').toUpperCase();
      return map[upper] || (type || '').toLowerCase() || 'table';
    };
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'x-access-token': new Auth().getAccessToken(),
    };

    const originalObjectMap = {};
    (originalDetails.schemas || []).forEach((schema) => {
      objectTypes.forEach((type) => {
        (schema[type] || []).forEach((obj) => {
          const objectId = obj.objectId || obj.object_id;
          if (objectId) {
            originalObjectMap[objectId] = obj;
          }
        });
      });
    });

    const originalColumnMap = {};
    Object.values(originalObjectMap).forEach((obj) => {
      (obj.columns || []).forEach((col) => {
        const columnId = col.columnId || col.column_id;
        if (columnId) {
          originalColumnMap[columnId] = col;
        }
      });
    });

    const promises = [];
    const updatedObjectIds = new Set();
    const updatedColumnIds = new Set();
    const originalInstance = originalDetails.instance || {};
    const instanceNameChanged = getValue(originalInstance.instanceName || originalInstance.instance_name) !== getValue(detailsToSave.instance.instanceName || detailsToSave.instance.instance_name);
    const instanceDescriptionChanged = getValue(originalInstance.description) !== getValue(detailsToSave.instance.description);

    if (instanceId && (instanceNameChanged || instanceDescriptionChanged)) {
      promises.push(
          axios.post(
              `${process.env.REACT_APP_API_URL}/dataDictionary/instance/${instanceId}`,
              {
                instanceName: detailsToSave.instance.instanceName || detailsToSave.instance.instance_name,
                instanceType: detailsToSave.instance.instanceType || detailsToSave.instance.instance_type,
                host: detailsToSave.instance.host,
                port: detailsToSave.instance.port,
                description: detailsToSave.instance.description,
                environment: detailsToSave.instance.environment,
              },
              {headers},
          ),
      );
    }

    (detailsToSave.schemas || []).forEach((schema) => {
      objectTypes.forEach((type) => {
        (schema[type] || []).forEach((obj) => {
          const objectId = obj.objectId || obj.object_id;
          if (!objectId || updatedObjectIds.has(objectId)) {
            return;
          }

          const originalObj = originalObjectMap[objectId];
          if (!originalObj) {
            return;
          }

          const businessContextChanged = getValue(originalObj.businessContext || originalObj.business_context) !== getValue(obj.businessContext || obj.business_context);
          const businessOwnerChanged = ddOwners(originalObj.businessOwner ?? originalObj.business_owner) !== ddOwners(obj.businessOwner ?? obj.business_owner);
          const techOwnerChanged = ddOwners(originalObj.techOwner ?? originalObj.tech_owner) !== ddOwners(obj.techOwner ?? obj.tech_owner);

          if (businessContextChanged || businessOwnerChanged || techOwnerChanged) {
            updatedObjectIds.add(objectId);
            promises.push(
                axios.put(
                    `${process.env.REACT_APP_API_URL}/dataDictionary/object/${objectId}`,
                    {
                      objectName: obj.objectName || obj.object_name || obj.tableName || obj.table_name || obj.name,
                      objectType: toDbObjectType(obj.objectType || obj.object_type || obj.tableType || obj.table_type),
                      description: obj.description ?? null,
                      businessContext: obj.businessContext || obj.business_context || '',
                      businessOwner: normalizeOwners(obj.businessOwner ?? obj.business_owner),
                      techOwner: normalizeOwners(obj.techOwner ?? obj.tech_owner),
                      rowCount: obj.rowCount || obj.row_count || 0,
                      sizeMb: obj.sizeMb ?? obj.size_mb ?? null,
                    },
                    {headers},
                ),
            );
          }

          (obj.columns || []).forEach((col) => {
            const columnId = col.columnId || col.column_id;
            if (!columnId || updatedColumnIds.has(columnId)) {
              return;
            }

            const originalCol = originalColumnMap[columnId];
            if (!originalCol) {
              return;
            }

            const columnBusinessContextChanged = getValue(originalCol.businessContext || originalCol.business_context) !== getValue(col.businessContext || col.business_context);
            const isPiiChanged = normalizeBool(originalCol.isPii ?? originalCol.is_pii) !== normalizeBool(col.isPii ?? col.is_pii);
            const isAiEligibleChanged = normalizeBool(originalCol.isAiEligible ?? originalCol.is_ai_eligible) !== normalizeBool(col.isAiEligible ?? col.is_ai_eligible);

            if (columnBusinessContextChanged || isPiiChanged || isAiEligibleChanged) {
              updatedColumnIds.add(columnId);
              promises.push(
                  axios.put(
                      `${process.env.REACT_APP_API_URL}/dataDictionary/column/${columnId}`,
                      {
                        columnName: col.columnName || col.column_name || col.name,
                        dataType: col.dataType || col.data_type,
                        isNullable: col.isNullable ?? col.is_nullable,
                        defaultValue: col.defaultValue ?? col.default_value ?? null,
                        businessContext: col.businessContext || col.business_context || '',
                        isPii: normalizeBool(col.isPii ?? col.is_pii),
                        isAiEligible: normalizeBool(col.isAiEligible ?? col.is_ai_eligible),
                      },
                      {headers},
                  ),
              );
            }
          });
        });
      });
    });

    this.setState({
      actionTriggered: true,
      triggeredItemName: instanceName,
      snackbarStatus: true,
    });

    const completeSave = () => {
      if (pendingImportPayload && instanceId) {
        this.props.importMissingObjects(instanceId, pendingImportPayload);
      }
      this.setState({
        actionTriggered: false,
        snackbarStatus: false,
        editedDictionaryDetails: null,
      }, () => this.updateSuccess());
    };
    const saveErrorMessage = `Failed to update ${promises.length} dictionary change${promises.length === 1 ? '' : 's'}.`;

    if (promises.length === 0) {
      completeSave();
      return;
    }

    Promise.all(promises)
        .then(() => {
          completeSave();
        })
        .catch((error) => {
          this.setState({
            actionTriggered: false,
            snackbarStatus: false,
            showBanner: true,
            bannerType: 'danger',
            bannerTitle: 'Error',
            bannerMessage: error?.response?.data?.error || error?.message || saveErrorMessage,
          });
        });
  };

  goBack = (level = 0) => {
    // Leaving the detail view is state-driven (pageState), so <Prompt> can't gate it.
    // Confirm explicitly through the same modal when there are unsaved edits.
    if (this.props.hasUnsavedChanges) {
      confirmNavigation(() => this.performGoBack(level));
      return;
    }
    this.performGoBack(level);
  };

  performGoBack = (level = 0) => {
    this.props.parentNavBar(1);
    if (level === 0) {
      const isSettingsPath = this.props.location?.pathname?.startsWith('/settings');
      if (this.props.history?.push && isSettingsPath) {
        this.props.history.push('/settings/datadictionary');
      }
      this.setState({
        pageState: 0,
        pageNavContainer: 'hide',
        pageNavContainerMain: 'page-nav-container',
        dictionaryId: null,
        editedDictionaryDetails: null,
      }, () => {
        this.goToTop();
      });
    }
  };

  render() {
    const {dictionaryList, isLoading, location} = this.props;


    return (
      <div className="sub-module">

        <Backdrop open={this.state.backdrop} data-testid="dd-backdrop"></Backdrop>

        <Snackbar
          open={this.state.snackbarStatus}
          message="Saving. Please wait..."
          className="snack-bar"
          ContentProps={{'data-testid': 'dd-snackbar'}}
        />


        <div className={this.state.pageNavContainer}>
          <a onClick={this.goBack.bind(this, 0)} className="back-link" data-testid="dd-back-link">
            <i className="material-icons">arrow_back</i>
            Manage Data Dictionary
          </a>
        </div>

        <Modal
          open={this.state.modalOpen}
          title={this.state.modalTitle}
          content={this.state.modalContent}
          primaryButton={this.state.modalPrimaryButton}
          secondaryButton={this.state.modalSecondaryButton}
          close={this.modalClose}
          confirm={this.modalConfirm}
        />

        <Banner
          type={this.state.bannerType}
          hasAlerts={this.state.showBanner}
          header={this.state.bannerTitle}
          body={this.state.bannerMessage}
          dismissBanner={this.dismissBanner.bind(this)}
          data-testid="dd-banner"
        />

        {
          this.state.pageState === 0 ?
          <ErrorBoundary>
            <h2>Manage Data Dictionary</h2>
            <ManageDataDictionary
              onClick={this.selectDictionary.bind(this)}
              dictionaryList={dictionaryList}
              isLoading={isLoading}
              orgId={orgId}
              deleteDictionary={this.deleteDictionary}
              onRefresh={(instanceId, dictionary) => {
                this.props.refreshWithDiff(instanceId);
                this.selectDictionary(dictionary);
              }}
            />
          </ErrorBoundary> :

          <ErrorBoundary>
            {this.state.dictionaryId ? (
              <DDDetailsView
                dictionaryDetails={this.state.editedDictionaryDetails || this.props.dictionaryDetails}
                isLoading={isLoading.includes('dictionarydetails')}
                onUpdateBusinessContext={this.handleUpdateBusinessContext}
                onUpdateOwner={this.handleUpdateOwner}
                onUpdateTechOwner={this.handleUpdateTechOwner}
                onUpdateItem={this.handleUpdateItem}
                onUpdateColumnIsPii={this.handleUpdateColumnIsPii}
                onUpdateColumnIsAiEligible={this.handleUpdateColumnIsAiEligible}
                onUpdateColumnBusinessContext={this.handleUpdateColumnBusinessContext}
                onSave={(updatedDetails, pendingImportPayload) =>
                  this.handleSaveDictionaryUpdates(updatedDetails, pendingImportPayload)
                }
                onCancel={this.goBack}
                userList={this.props.userList}
                techOwnerList={this.props.techOwnerList}
                fetchUserList={this.props.fetchUserList}
                fetchTechOwnerList={this.props.fetchTechOwnerList}
                pendingChanges={this.props.pendingChanges}
                isRefreshing={this.props.isRefreshing}
                onRefreshSchema={(instanceId) => {
                  // Reload of pending changes is chained to the refresh completing
                  // (see the isRefreshing watcher in componentDidUpdate), not a fixed
                  // timer — the backend writes N change requests over a few seconds and
                  // a fixed delay read a partial set.
                  this.props.refreshWithDiff(instanceId);
                }}
                onWorkflowAction={(requestId, action, comments) => {
                  this.props.applyWorkflowAction(requestId, action, comments);
                  const instanceId = this.state.dictionaryId;
                  setTimeout(() => this.props.loadPendingChanges(instanceId), 1000);
                }}
                onRollbackChangeRequest={(requestId, reason) => {
                  this.props.rollbackChangeRequest(requestId, reason);
                  const instanceId = this.state.dictionaryId;
                  setTimeout(() => this.props.loadPendingChanges(instanceId), 1000);
                }}
                onNavigateToWorkflow={() => this.props.history && this.props.history.push('/workflow')}
                missingItems={this.props.missingItems}
                isLoadingMissing={this.props.isLoadingMissing}
                isImportingMissing={this.props.isImportingMissing}
                onLoadMissingObjects={(instanceId) => this.props.loadMissingObjects(instanceId)}
                onPreviewMissingAttributes={(instanceId, selectedObjects) => this.props.previewMissingAttributes(instanceId, selectedObjects)}
                onImportMissingObjects={(instanceId, payload) => {
                  this.props.importMissingObjects(instanceId, payload);
                }}
              />
            ) : (
              <DDWizard
                onSave={this.handleWizardSave}
                onCancel={this.handleWizardCancel}
              />
            )}
          </ErrorBoundary>
        }

      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  dictionaryList: state.dataDictionaryReducer.dictionaryList,
  isLoading: state.dataDictionaryReducer.isLoading,
  dictionaryDetails: state.dataDictionaryReducer.dictionaryDetails,
  error: state.dataDictionaryReducer.error,
  userList: state.dataDictionaryReducer.userList || [],
  techOwnerList: state.dataDictionaryReducer.techOwnerList || [],
  pendingChanges: state.dataDictionaryReducer.pendingChanges || [],
  isRefreshing: state.dataDictionaryReducer.isRefreshing || false,
  refreshResult: state.dataDictionaryReducer.refreshResult,
  rollbackResult: state.dataDictionaryReducer.rollbackResult,
  rollbackHistory: state.dataDictionaryReducer.rollbackHistory || [],
  deleteResult: state.dataDictionaryReducer.deleteResult,
  missingItems: state.dataDictionaryReducer.missingItems,
  isLoadingMissing: state.dataDictionaryReducer.isLoadingMissing || false,
  importMissingResult: state.dataDictionaryReducer.importMissingResult,
  isImportingMissing: state.dataDictionaryReducer.isImportingMissing || false,
  hasUnsavedChanges: state.commonReducer ? state.commonReducer.hasUnsavedChanges : false,
});

const mapDispatchToProps = {
  loadDictionaryList,
  deleteDictionary,
  loadDictionaryDetails,
  updateDictionaryInstance,
  importSchema,
  fetchUserList,
  fetchTechOwnerList,
  refreshWithDiff,
  loadPendingChanges,
  applyWorkflowAction,
  rollbackChangeRequest,
  loadRollbackHistory,
  updateColumnIsPii,
  updateColumnIsAiEligible,
  loadMissingObjects,
  previewMissingAttributes,
  importMissingObjects,
  setUnsavedChanges,
  clearUnsavedChanges,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DataDictionary));
